import { useCallback } from "react";
import { Languages } from "lucide-react";
import { useAtom } from "jotai";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { languageAtom } from "@/state/language";
import { useUser } from "@/hooks/useUser";
import { useUpdateUser } from "@/hooks/auth-hooks";

// Supported languages for the language switcher
const SUPPORTED_LANGUAGES = [
  { value: "en", label: "English", flag: "🇺🇸" },
  { value: "nl", label: "Nederlands", flag: "🇳🇱" },
] as const;

export function LanguageSwitcher() {
  const [language, setLanguage] = useAtom(languageAtom);
  const { user, isAuthenticated } = useUser();
  const { mutateAsync } = useUpdateUser();

  const handleLanguageChange = useCallback(
    async (newLanguage: string) => {
      // Update local state immediately
      setLanguage(newLanguage);

      // If user is authenticated, also update their profile
      if (isAuthenticated && user?.id) {
        try {
          await mutateAsync({
            language: newLanguage,
          });
        } catch (error) {
          console.error("Failed to update user language:", error);
          // Revert local state if server update fails
          setLanguage(language);
        }
      }
    },
    [language, setLanguage, isAuthenticated, user?.id, mutateAsync],
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="group/toggle size-8 px-0"
          aria-label="Switch interface language"
        >
          <Languages className="size-4" />
          <span className="sr-only">Switch interface language</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[140px]">
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.value}
            onClick={() => handleLanguageChange(lang.value)}
            className={`flex items-center gap-2 cursor-pointer ${
              language === lang.value ? "bg-accent" : ""
            }`}
          >
            <span className="text-base">{lang.flag}</span>
            <span className="flex-1">{lang.label}</span>
            {language === lang.value && <span className="text-xs text-muted-foreground">✓</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
